import { acceptHMRUpdate } from 'pinia'

import {
  type RoleEnum,
  type UserProfileRoleOut,
  getCurrentUserProfilePassportIdmIdmGetCurrentUserPost,
} from '@/shared/api/intranet'

const PROFILE_STORAGE_KEY = 'user_profile'
const PROFILE_TIMESTAMP_KEY = 'user_profile_timestamp'
const PROFILE_TTL = 1000 * 60 * 60 // 1 час

function isProfileCacheValid(): boolean {
  const timestamp = localStorage.getItem(PROFILE_TIMESTAMP_KEY)
  if (!timestamp) {
    return false
  }

  const age = Date.now() - Number.parseInt(timestamp, 10)
  return age < PROFILE_TTL
}

function loadProfileFromStorage(): UserProfileRoleOut | null {
  try {
    if (!isProfileCacheValid()) {
      localStorage.removeItem(PROFILE_STORAGE_KEY)
      localStorage.removeItem(PROFILE_TIMESTAMP_KEY)
      return null
    }

    const storedProfile = localStorage.getItem(PROFILE_STORAGE_KEY)
    return storedProfile ? JSON.parse(storedProfile) : null
  } catch (error) {
    console.error('Failed to load profile from storage:', error)
    return null
  }
}

function saveProfileToStorage(profile: UserProfileRoleOut | null) {
  try {
    if (profile) {
      localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profile))
      localStorage.setItem(PROFILE_TIMESTAMP_KEY, Date.now().toString())
    } else {
      localStorage.removeItem(PROFILE_STORAGE_KEY)
      localStorage.removeItem(PROFILE_TIMESTAMP_KEY)
    }
  } catch (error) {
    console.error('Failed to save profile to storage:', error)
  }
}

export const useProfile = defineStore('profile', () => {
  // Инициализация из localStorage
  const initialProfile = loadProfileFromStorage()

  const profile = ref<UserProfileRoleOut | null>(initialProfile)
  const profileIsLoading = ref(false)
  const isInitialized = ref(false)

  const router = useRouter()

  // Watch profile changes and persist to localStorage
  watch(profile, saveProfileToStorage, { deep: true })

  /**
   * Очищает локальное состояние пользователя без запроса к серверу.
   * Используется когда сессия уже невалидна (401) или отсутствует.
   */
  function clearUserState() {
    profile.value = null
    isInitialized.value = false
    saveProfileToStorage(null)
  }

  /**
   * Обрабатывает ошибки при получении профиля.
   * Очищает состояние и перенаправляет на страницу входа.
   */
  async function handleProfileError(error: unknown) {
    const isSignInPage = router.currentRoute.value.path.includes('/auth/sign-in')
    if (!error || isSignInPage) {
      return
    }

    clearUserState()

    const redirectUrl = '/auth/sign-in'

    await router.push(redirectUrl)
  }

  /**
   * Инициализирует профиль пользователя при старте приложения
   * Использует кэш, если он валиден, иначе загружает с сервера
   */
  async function initProfile(): Promise<UserProfileRoleOut | null> {
    if (isInitialized.value) {
      return profile.value
    }

    // Если есть валидный кэш, используем его и фоново загружаем с сервера
    if (profile.value && isProfileCacheValid()) {
      isInitialized.value = true
      getUserProfile()
      return profile.value
    }

    // Иначе загружаем с сервера
    await getUserProfile()
    isInitialized.value = true
    return profile.value
  }

  /**
   * Загружает профиль пользователя с сервера
   */
  async function getUserProfile() {
    profileIsLoading.value = true
    const { data, error } = await getCurrentUserProfilePassportIdmIdmGetCurrentUserPost()
    profileIsLoading.value = false

    await handleProfileError(error)
    profile.value = data || null

    return profile.value
  }

  /**
   * Обновляет профиль с сервера (принудительно, игнорируя кэш)
   */
  async function refreshProfile() {
    return getUserProfile()
  }

  /**
   * Выполняет logout на сервере и очищает состояние.
   * Используется только для явного выхода пользователя (кнопка "Выйти").
   */
  async function logout(redirectUrl: string = '/auth/sign-in') {
    const origin = window.location.origin
    try {
      await fetch(`${origin}/auth-service/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      })
    } catch (error) {
      console.error('Logout request failed:', error)
    }

    clearUserState()
    await router.push(redirectUrl)
  }

  // Computed properties
  const role = computed<Nullable<RoleEnum>>(() => profile.value?.role || null)
  const isAdmin = computed(() => role.value === 'ADMIN')
  const isShopManager = computed(() => role.value === 'SHOP_MANAGER')
  const isContentManager = computed(() => role.value === 'CONTENT_MANAGER')
  const corpId = computed(() => profile.value?.id || '')
  const isLoggedIn = computed(() => !!profile.value)
  const firstName = computed(() => profile.value?.firstName || '')
  const lastName = computed(() => profile.value?.lastName || '')
  const fullName = computed(() => `${firstName.value} ${lastName.value}`)
  const isProbationPassed = computed(() => profile.value?.isProbationPassed || false)
  const userCountry = computed(() => profile.value?.office?.city?.country)

  /**
   * Сбрасывает состояние стора
   */
  const $reset = () => {
    clearUserState()
    profileIsLoading.value = false
  }

  return {
    // State
    profile,
    profileIsLoading,
    isInitialized,

    // Actions
    initProfile,
    getUserProfile,
    refreshProfile,
    logout,
    $reset,

    // Computed
    isLoggedIn,
    isAdmin,
    isShopManager,
    isContentManager,
    role,
    corpId,
    fullName,
    userCountry,
    isProbationPassed,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useProfile, import.meta.hot))
}
